import { useMemo, FC, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Column, CellProps } from 'react-table';
import { observer } from 'mobx-react-lite';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { getDate } from 'utils/convertString';
import { useStore } from 'hooks/useRootStore';
import { useDebounce } from 'hooks/useDebounce';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { PAGE_SIZE, Table } from 'components/Core/Table';
import { NoDataComponent } from 'components/Core/NoData/NoData';
import { StatusCell } from 'components/Core/StatusCell';
import { ComponentWithTooltip } from 'components/Core/ComponentWithTooltip';
import { ControlledInput } from 'components/Inputs/ControlledInput';
import { AppLoader } from 'components/Core/AppLoader/AppLoader';
import { IScriptsList } from 'stores/scripts/types';
import { StatusTypeReq } from 'types/common';
import { ReactComponent as EditIcon } from 'assets/edit_icon.svg';
import { ReactComponent as ParsersIcon } from 'assets/navbar/navbar_sync.svg';
import { scriptsTableTranslations } from './translations';
import { ScriptsTableFiltersForm, ScriptsTableTransFields } from './types';
import styles from './scriptsTable.module.scss';

const ScriptsTable: FC = () => {
  const {
    scriptsStore: { scriptsList, changeScriptStatus, resyncScript, getScriptsList },
    mainStore: { user },
    alertStore: { showAlert },
  } = useStore();

  const [filtered, setFiltered] = useState<IScriptsList[]>([]);

  const navigate = useNavigate();

  const { t } = useMyTranslations<ScriptsTableTransFields>([scriptsTableTranslations.name]);

  const { mutate, isLoading: isFetching } = useMutation(getScriptsList, {
    mutationKey: 'getScriptsList',
    retry: false,
  });

  const { mutate: resyncMutate, isLoading } = useMutation(resyncScript, {
    mutationKey: 'resyncScript',
    onSuccess: () => {
      showAlert(t('resyncScriptsSuccess'), 'success');
    },
  });

  useEffect(() => {
    mutate();
  }, [user?.selectedCountryCode]);

  const useFormMethods = useForm<ScriptsTableFiltersForm>();

  const { watch } = useFormMethods;
  const searchD = useDebounce(watch('search'));

  useEffect(() => {
    const list = scriptsList || [];
    setFiltered(searchD ? list.filter((item) => item.name.toLowerCase().includes(searchD.toLowerCase())) : list);
  }, [searchD, scriptsList]);

  const onEditClick = useCallback(
    (item: IScriptsList) => {
      navigate(`${item.type}/${item.id}`);
    },
    [navigate]
  );

  const columns: Column<IScriptsList>[] = useMemo(
    () => [
      {
        Header: t('name'),
        id: 'name',
        Cell: ({
          row: {
            original,
            original: { name, completeStatus },
          },
        }: CellProps<IScriptsList>) => (
          <button
            className={styles.avatar}
            onClick={() => onEditClick(original)}
            disabled={isLoading || completeStatus === 'in-progress'}>
            <p className={styles.name}>{name}</p>
          </button>
        ),
        accessor: ({ name }) => name,
        width: 10,
      },
      {
        Header: t('lastSyncDate'),
        id: 'lastSyncDate',
        Cell: ({
          row: {
            original: { lastSyncDate },
          },
        }: CellProps<IScriptsList>) => (
          <p className={styles.date}>{lastSyncDate ? getDate(lastSyncDate, true) : '—'}</p>
        ),
        accessor: ({ lastSyncDate }) => (lastSyncDate ? +new Date(lastSyncDate) : 0),
        width: 4,
      },
      {
        Header: t('completeStatus'),
        id: 'completeStatus',
        accessor: ({ completeStatus }) => completeStatus ?? '—',
        width: 4,
      },
      {
        Header: t('log'),
        id: 'log',
        Cell: ({
          row: {
            original: { log },
          },
        }: CellProps<IScriptsList>) => <p className={styles.log}>{log ?? '—'}</p>,
        accessor: ({ log }) => log ?? '—',
        width: 4,
      },
      {
        Header: t('status'),
        id: 'status',
        Cell: ({
          row: {
            original: { id, type, status, completeStatus },
          },
        }: CellProps<IScriptsList>) => (
          <StatusCell
            id={id}
            status={status}
            changeStatus={({ id: rowId, status: rowStatus }: StatusTypeReq) =>
              changeScriptStatus({ id: rowId, type, status: rowStatus })
            }
            disabled={isLoading || completeStatus === 'in-progress'}
          />
        ),
        accessor: ({ status }) => status,
        width: 3,
      },
      {
        Header: t('option'),
        id: 'option',
        Cell: ({
          row: {
            original,
            original: { id, type, completeStatus, status },
          },
        }: CellProps<IScriptsList>) => (
          <div className={styles.actionBoxBtn}>
            <ComponentWithTooltip
              component={
                <button
                  className={styles.editBtn}
                  onClick={() => onEditClick(original)}
                  disabled={isLoading || completeStatus === 'in-progress'}>
                  <EditIcon />
                </button>
              }
              name='editIcon'
              message={t('edit')}
              place='top'
            />
            <ComponentWithTooltip
              component={
                <button
                  className={styles.editBtn}
                  onClick={() => resyncMutate({ id, type })}
                  disabled={isLoading || completeStatus === 'in-progress' || status !== 'Active'}>
                  <ParsersIcon width={16} height={16} />
                </button>
              }
              name='syncIcon'
              message={t('resync')}
              place='top'
            />
          </div>
        ),
        width: 3,
        disableSortBy: true,
      },
    ],
    // eslint-disable-next-line
    [isLoading, filtered]
  );

  columns.forEach((item) => (item.Filter = () => null));

  return (
    <>
      <FormProvider {...useFormMethods}>
        <div className={styles.search}>
          <ControlledInput name='search' classNameProp={styles.searchInput} placeholder={t('search')} isRemoveMargin />
        </div>
      </FormProvider>
      {isFetching ? (
        <AppLoader />
      ) : scriptsList && scriptsList.length > 0 ? (
        <Table
          columns={columns as never}
          data={[...filtered]}
          simpleList
          isPaginated={scriptsList.length > PAGE_SIZE}
        />
      ) : (
        <NoDataComponent />
      )}
    </>
  );
};

export default observer(ScriptsTable);
